export const SCREEN_SHARE_QUALITY_IDS = ['sd', 'hd', 'fhd'] as const

export type ScreenShareQualityId = typeof SCREEN_SHARE_QUALITY_IDS[number]

export const DEFAULT_SCREEN_SHARE_QUALITY: ScreenShareQualityId = 'fhd'

/** Capture + encoder profile. FPS stays modest — text/scroll need resolution more than motion. */
export const SCREEN_SHARE_QUALITY_PRESETS: Record<ScreenShareQualityId, {
  width: number
  height: number
  maxBitrate: number
  maxFramerate: number
}> = {
  sd: { width: 640, height: 480, maxBitrate: 800_000, maxFramerate: 10 },
  hd: { width: 1280, height: 720, maxBitrate: 1_500_000, maxFramerate: 12 },
  fhd: { width: 1920, height: 1080, maxBitrate: 2_500_000, maxFramerate: 15 },
}

const screenShareCaptureFrameRate = { ideal: 10, max: 15 }

/**
 * Native screen capture ceiling from WUI-5277. Do not lock max to the preset:
 * getDisplayMedia then downscales a 2560×1440 / ultrawide source to 16:9 1080p
 * and stops following the picked display.
 */
const SCREEN_SHARE_CAPTURE_MAX_WIDTH = 2560
const SCREEN_SHARE_CAPTURE_MAX_HEIGHT = 1440

export const isScreenShareQualityId = (value: unknown): value is ScreenShareQualityId => {
  return typeof value === 'string' && (SCREEN_SHARE_QUALITY_IDS as readonly string[]).includes(value)
}

export const parseScreenShareQualityId = (value: unknown): ScreenShareQualityId => {
  return isScreenShareQualityId(value) ? value : DEFAULT_SCREEN_SHARE_QUALITY
}

export const getScreenShareVideoConstraints = (quality: ScreenShareQualityId): MediaTrackConstraints => {
  const preset = SCREEN_SHARE_QUALITY_PRESETS[parseScreenShareQualityId(quality)]
  return {
    width: { ideal: preset.width, max: SCREEN_SHARE_CAPTURE_MAX_WIDTH },
    height: { ideal: preset.height, max: SCREEN_SHARE_CAPTURE_MAX_HEIGHT },
    frameRate: screenShareCaptureFrameRate,
  }
}

export const SCREEN_SHARE_QUALITY_I18N_KEYS: Record<ScreenShareQualityId, string> = {
  sd: 'soundAndVideo.webcamQualitySd',
  hd: 'soundAndVideo.webcamQualityHd',
  fhd: 'soundAndVideo.webcamQualityFhd',
}
